import React, { useEffect, useState, Suspense, lazy, Fragment, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import store from '@src/store'
import { createBrowserRouter, RouterProvider, BrowserRouter, Route, Routes, useNavigate, useLocation, useRouteError, useLoaderData } from 'react-router'
import { Provider, useSelector, useDispatch } from 'react-redux'
import { closeSidebar, setCookiesAccepted, setScrollPosition, toggleSidebarLocked, closePricing, toggleSidebar, pingLocalStoreThunk } from '@src/mainSlice'
import { useGetCurrentQuery } from '@src/pages/stores/storesApi'
import { logout, getUserThunk } from '@pages/login/authSlice'
import { mainApiUrl, storeId } from '@constants'
import { setSearch, setAlbum } from '@components/filters/filtersSlice'
import { I18n, t, getLocale } from 'react-i18nify'
import { initBreakpoints, useBreakpoints } from 'react-match-breakpoints'
import { bugsnagNotify, bugsnagClient, getLocale as getLocaleUtil, setLocale, ucFirst, bugsnagSetUser } from '@src/utils'
import { LoaderCircle, TriangleAlert, Info, Link2Off } from "lucide-react"
import storesApi from '@src/pages/stores/storesApi'
import LangFlags from '@components/LangFlags';
import { useBus, busDispatch } from '@hooks'
import { useDebouncedCallback } from 'use-debounce';
import { Button } from "@/components/ui/button"
import pRetry from 'p-retry'
import ms from 'ms'

import 'react-day-picker/style.css'
import 'react-international-phone/style.css'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'
import '@yaireo/tagify/dist/tagify.css'
import './main.css'

import('@translations').then(({ initTranslations }) => initTranslations())

const AllOrders = lazy(() => import('@pages/orders/Orders'))
const UserOrders = lazy(() => import('@pages/orders/UserOrders'))

const LoaderComponent = () => <div className="m-auto flex"><LoaderCircle className="m-auto animate-spin size-24 opacity-80 text-white" strokeWidth={1} /></div>

const Orders = () => {
  const { user: authUser } = useSelector(({ auth }) => auth)

  if (authUser.role === 'user') {
    return <UserOrders />
  }

  return <AllOrders />
}

const breakpointsConfig = {
  xxs: 'screen and (min-width: 320px)',
  xs: 'screen and (min-width: 480px)',
  sm: 'screen and (min-width: 640px)',
  md: 'screen and (min-width: 768px)',
  lg: 'screen and (min-width: 1024px)',
  xl: 'screen and (min-width: 1280px)',
  xl2: 'screen and (min-width: 1536px)',
}

const BreakpointsProvider = initBreakpoints(breakpointsConfig)
const ErrorBoundary = bugsnagClient.getPlugin('react').createErrorBoundary(React)

const FallbackComponent = ({ data }) => {
  const location = useLocation()
  const locale = getLocale()
  const error = useRouteError() || {};
  const isDev = bugsnagClient._config.releaseStage === 'development'

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const localeId = params.get('locale')

    setLocale(localeId || getLocaleUtil())
  }, [])

  if (!error && !data) {
    return (
      <div className="flex min-h-screen justify-center items-center w-full">
        <div className="flex bg-white justify-center items-center px-6 py-2 rounded-lg cursor-pointer" onClick={() => window.location.replace('/')}>
          <TriangleAlert className="w-10 h-10 text-red-700" />
          <div className="mx-4 text-md">
            <p className="">{t('error_loading')}</p>
            <p className="font-bold">{t('please_reload')}</p>
          </div>
        </div>
      </div>
    )
  }

  if (data && isDev) {
    return (
      <div className="flex min-h-screen bg-black w-full">
        <div className="m-auto">
          <pre className="my-2 font-bold text-red-500">{data.error.toString()}</pre>
          <pre className="w-full text-white">{data.info.componentStack}</pre>
        </div>
      </div>
    )
  }

  if (error.status === 404) {
    return (
      <div className="flex min-h-screen justify-center items-center w-full">
        <div className="flex bg-white justify-center items-center px-6 py-2 rounded-lg cursor-pointer" onClick={() => window.location.replace('/')}>
        <Link2Off className="w-10 h-10 text-blue-500" />
        <div className="mx-4 text-md">
          <p className="">{t('not_found')}</p>
        </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen justify-center items-center w-full px-4">
      <div className="flex bg-white justify-center items-center px-6 py-2 rounded-lg cursor-pointer" onClick={() => window.location.replace('/')}>
        <TriangleAlert className="w-10 h-10 text-red-700" />
        <div className="mx-4 text-md">
          <p className="">{isDev ? (error.statusText || error.message || '?') : t('error_loading')}</p>
        </div>
      </div>
    </div>
  )
}

const container = document.getElementById('root');
const root = createRoot(container)

const storeLoader = async ({ request, params }) => {
  const storeFetcher = async () => {
    try {
      return await store.dispatch(storesApi.endpoints.getCurrent.initiate(undefined, { forceRefetch: true })).unwrap()
    } catch (e) {
      if (e && e.status === 404) {
        window.location.replace('/not_found')
      }
      bugsnagNotify(e, event => { event.addMetadata('metadata', { request, params }) })
      throw new Error('Error fetching Store data!')
    }
  }

  return { storeData: await pRetry(storeFetcher, { retries: 2 }) }
}

const router = createBrowserRouter([
  {
    path: "/",
    Component: lazy(() => import('@pages/layout')),
    children: [
      {
        path: "",
        Component: lazy(() => import('@pages/home/Home')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "albums/:id",
        Component: lazy(() => import('@pages/home/Home')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "login",
        Component: lazy(() => import('@pages/login/Login')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "media",
        Component: lazy(() => import('@pages/media/Media')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "help",
        Component: lazy(() => import('@pages/help/Help')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "signup",
        Component: lazy(() => import('@pages/signup/SignUp')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "sync",
        Component: lazy(() => import('@pages/sync/Sync')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "profile",
        Component: lazy(() => import('@pages/profile/Profile')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "cart",
        Component: lazy(() => import('@pages/cart/Cart')),
        HydrateFallback: LoaderComponent,
        loader: async ({ request, params }) => {
          const output = await storeLoader({ request, params })

          await store.dispatch(pingLocalStoreThunk({ timeout: ms('2s') }))

          return output
        },
      },
      {
        path: "store",
        Component: lazy(() => import('@pages/store/Store')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "logs",
        Component: lazy(() => import('@pages/logs/Logs')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "dashboard",
        Component: lazy(() => import('@pages/dashboard/Dashboard')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "addons",
        Component: lazy(() => import('@pages/addons/AddOns')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "carts",
        Component: lazy(() => import('@pages/carts/Carts')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "coupons",
        Component: lazy(() => import('@pages/coupons/Coupons')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "users",
        Component: lazy(() => import('@pages/users/Users')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "orders",
        Component: Orders,
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "stats",
        Component: lazy(() => import('@pages/stats/Stats')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "albums",
        Component: lazy(() => import('@pages/albums/Albums')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "upload",
        Component: lazy(() => import('@pages/upload/Upload')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
      {
        path: "costs",
        Component: lazy(() => import('@pages/costs/Costs')),
        HydrateFallback: LoaderComponent,
        loader: storeLoader,
      },
    ],
    HydrateFallback: LoaderComponent,
    loader: storeLoader,
    errorElement: <I18n render={(...props) => <FallbackComponent {...props} />} />
  },
  {
    path: "tv",
    Component: lazy(() => import('@pages/tv/Tv')),
    HydrateFallback: LoaderComponent,
    loader: async ({ request, params }) => {
      const { data } = await store.dispatch(storesApi.endpoints.getCurrent.initiate(undefined, { forceRefetch: false }))

      return { storeData: data }
    },
  },
]);

root.render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={(data) => <I18n render={() => <FallbackComponent data={data} />} />}>
      <Provider store={store}>
        <BreakpointsProvider>
            <div data-slot="sidebar-wrapper"className="group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex flex-wrap min-h-lvh w-full">
              <RouterProvider router={router} />
            </div>
        </BreakpointsProvider>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
)
